import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import { ChevronsUpDown, Check, Plus } from 'lucide-react';
import { cn } from '@/lib/utils';

interface Workspace {
  id: string;
  name: string;
  slug: string;
  role: string;
}

interface WorkspacesResponse {
  workspaces: Workspace[];
}

export function WorkspaceSwitcher(): React.JSX.Element {
  const [selectedWorkspace, setSelectedWorkspace] = React.useState<Workspace | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['workspaces'],
    queryFn: () => api.get<WorkspacesResponse>('/workspaces'),
    staleTime: 5 * 60 * 1000,
  });

  const workspaces = React.useMemo(() => data?.workspaces || [], [data?.workspaces]);

  React.useEffect(() => {
    if (workspaces.length > 0 && !selectedWorkspace) {
      const firstWorkspace = workspaces[0];
      if (firstWorkspace) {
        setSelectedWorkspace(firstWorkspace);
      }
    }
  }, [workspaces, selectedWorkspace]);

  if (isLoading) {
    return (
      <Button variant="outline" className="w-[200px] justify-between" disabled>
        <span className="text-muted-foreground">Loading...</span>
      </Button>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" className="w-[200px] justify-between">
          <span className="truncate">
            {selectedWorkspace?.name || 'Select workspace'}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-[200px]">
        <DropdownMenuLabel>Workspaces</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {workspaces.map((workspace) => (
          <DropdownMenuItem
            key={workspace.id}
            onClick={() => setSelectedWorkspace(workspace)}
            className="flex items-center justify-between"
          >
            <div className="flex flex-col">
              <span className="truncate">{workspace.name}</span>
              <span className="text-xs text-muted-foreground capitalize">
                {workspace.role}
              </span>
            </div>
            {selectedWorkspace?.id === workspace.id && (
              <Check className={cn('h-4 w-4')} />
            )}
          </DropdownMenuItem>
        ))}
        {workspaces.length === 0 && (
          <DropdownMenuItem disabled>
            <span className="text-muted-foreground">No workspaces</span>
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem>
          <Plus className="mr-2 h-4 w-4" />
          Create Workspace
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
